'use client';

import React, { useEffect, useState } from 'react';
import { BlogCategoryPill } from './useBlogGrid';

const ArrowSVG = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 25 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12.5" cy="12.5" r="12.5" fill="#228E71" />
    <path d="M11 8L16 12.5L11 17" stroke="white" strokeWidth="2" />
  </svg>
);

interface BlogCategoriesBarProps {
  pills: BlogCategoryPill[];
  activeCategory: string;
  allLabel: string;
  scrollLeftLabel: string;
  scrollRightLabel: string;
  tabsRef: React.RefObject<HTMLDivElement>;
  onSelect: (slug: string) => void;
  onScrollLeft: () => void;
  onScrollRight: () => void;
}

export default function BlogCategoriesBar({
  pills,
  activeCategory,
  allLabel,
  scrollLeftLabel,
  scrollRightLabel,
  tabsRef,
  onSelect,
  onScrollLeft,
  onScrollRight,
}: BlogCategoriesBarProps) {
  const pillClass = (active: boolean) =>
    `flex min-w-[11rem] shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-[3.25rem] font-geist text-[2rem] tracking-[-0.08rem] max-lg:py-[1.75rem] max-lg:leading-normal lg:h-[6.125rem] lg:py-0 lg:leading-none transition-colors ${
      active
        ? 'border-transparent bg-[#003828] font-semibold text-white'
        : 'border-black/10 bg-white font-medium text-black/60 hover:border-black/30 hover:text-black'
    }`;

  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const update = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tabsRef, pills.length]);

  return (
    <div className="flex min-w-0 items-center lg:gap-[2.75rem]">
      {overflowing && (
        <button
          type="button"
          onClick={onScrollLeft}
          className="shrink-0 max-lg:hidden"
          aria-label={scrollLeftLabel}
        >
          <ArrowSVG className="h-[3.125rem] w-[3.125rem] rotate-180" />
        </button>
      )}

      <div
        ref={tabsRef}
        className="flex min-w-0 flex-1 items-center gap-[1.25rem] overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-lg:-mx-[2rem] max-lg:pl-[2rem] max-lg:after:w-[0.75rem] max-lg:after:shrink-0 max-lg:after:content-['']"
      >
        <button
          type="button"
          onClick={() => onSelect('')}
          aria-pressed={activeCategory === ''}
          className={pillClass(activeCategory === '')}
        >
          {allLabel}
        </button>
        {pills.map((pill) => (
          <button
            key={pill.slug}
            type="button"
            onClick={() => onSelect(pill.slug)}
            aria-pressed={activeCategory === pill.slug}
            className={pillClass(activeCategory === pill.slug)}
          >
            {pill.label}
          </button>
        ))}
      </div>

      {overflowing && (
        <button
          type="button"
          onClick={onScrollRight}
          className="shrink-0 max-lg:hidden"
          aria-label={scrollRightLabel}
        >
          <ArrowSVG className="h-[3.125rem] w-[3.125rem]" />
        </button>
      )}
    </div>
  );
}
